// src/components/EQTest.tsx
import React, { useState, useMemo } from "react";
import { Card, Button, Progress, Typography, Result, Row, Col } from "antd";
import {
  RedoOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { questions, results, Question, ResultLevel } from "../data/questions";

const { Title, Paragraph } = Typography;

// Hàm xáo trộn mảng (Fisher-Yates Shuffle)
const shuffleArray = <T,>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const EQTest: React.FC = () => {
  // State này dùng để kích hoạt lại việc random khi user bấm "Làm lại"
  const [testSessionId, setTestSessionId] = useState<number>(0);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [userAnswers, setUserAnswers] = useState<Record<number, number>>({});
  const [isFinished, setIsFinished] = useState<boolean>(false);

  // LOGIC RANDOM MỚI:
  // 1. Xáo trộn thứ tự câu hỏi (questions)
  // 2. Xáo trộn thứ tự đáp án (options)
  // 3. Phụ thuộc vào [testSessionId] để tính toán lại khi reset
  const shuffledQuestions = useMemo(() => {
    // Bước 1: Xáo trộn danh sách câu hỏi gốc
    const randomQuestions = shuffleArray(questions);

    // Bước 2: Xáo trộn đáp án bên trong từng câu hỏi
    return randomQuestions.map((q) => ({
      ...q,
      options: shuffleArray(q.options),
    }));
  }, [testSessionId]); // eslint-disable-line

  // Xử lý chọn đáp án
  const handleSelectOption = (score: number) => {
    setUserAnswers((prev) => ({
      ...prev,
      [currentQuestionIndex]: score,
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < shuffledQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      setIsFinished(true);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleRestart = () => {
    // Tăng testSessionId để trigger useMemo chạy lại logic random
    setTestSessionId((prev) => prev + 1);

    // Reset các state khác về ban đầu
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setIsFinished(false);
  };

  const calculateTotalScore = () => {
    return Object.values(userAnswers).reduce((sum, score) => sum + score, 0);
  };

  const getResult = (): ResultLevel => {
    const totalScore = calculateTotalScore();
    return (
      results.find((r) => totalScore >= r.min && totalScore <= r.max) ||
      results[0]
    );
  };

  // --- MÀN HÌNH KẾT QUẢ ---
  if (isFinished) {
    const totalScore = calculateTotalScore();
    const finalResult = getResult();

    let statusIconColor = "#ff8e53";
    if (finalResult.color === "success") statusIconColor = "#52c41a";
    if (finalResult.color === "error") statusIconColor = "#ff4d4f";

    return (
      <Card
        bordered={false}
        className="fade-in"
        style={{
          marginTop: 20,
          borderRadius: 24,
          boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
          textAlign: "center",
          padding: "20px",
          background: "rgba(255, 255, 255, 0.9)",
        }}
      >
        <Result
          icon={<CheckCircleFilled style={{ color: statusIconColor }} />}
          title={
            <div style={{ fontFamily: "'Work Sans', sans-serif" }}>
              <div
                style={{ fontSize: "18px", color: "#8c8c8c", marginBottom: 5 }}
              >
                Tổng điểm EQ của bạn
              </div>
              <div
                style={{
                  fontSize: "64px",
                  fontWeight: 700,
                  color: statusIconColor,
                  lineHeight: 1,
                }}
              >
                {totalScore}
              </div>
            </div>
          }
          subTitle={
            <div style={{ marginTop: 20 }}>
              <Title level={3} style={{ color: "#434343" }}>
                {finalResult.title}
              </Title>
            </div>
          }
          extra={[
            <div
              key="desc"
              style={{
                background: "#fff7e6",
                padding: "24px",
                borderRadius: "16px",
                marginBottom: "30px",
                textAlign: "left",
                border: "1px solid #ffe7ba",
              }}
            >
              <Paragraph
                style={{
                  fontSize: "16px",
                  margin: 0,
                  color: "#595959",
                  lineHeight: 1.8,
                }}
              >
                {finalResult.description}
              </Paragraph>
            </div>,
            <Button
              type="primary"
              size="large"
              key="console"
              onClick={handleRestart}
              icon={<RedoOutlined />}
              shape="round"
              style={{ paddingLeft: 40, paddingRight: 40, height: 50 }}
            >
              Làm lại bài kiểm tra (Đề mới)
            </Button>,
          ]}
        />
      </Card>
    );
  }

  // --- MÀN HÌNH CÂU HỎI ---
  const currentQuestion: Question = shuffledQuestions[currentQuestionIndex];

  const progressPercent = Math.round(
    ((currentQuestionIndex + 1) / shuffledQuestions.length) * 100
  );
  const currentSelectedScore = userAnswers[currentQuestionIndex];

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 15, padding: "0 10px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 5,
            fontSize: "14px",
            color: "#8c8c8c",
            fontWeight: 500,
          }}
        >
          <span>Tiến độ</span>
          <span>{Math.round(progressPercent)}%</span>
        </div>
        <Progress
          percent={progressPercent}
          showInfo={false}
          strokeColor={{ "0%": "#ff9c6e", "100%": "#ffc069" }}
          trailColor="#f0f0f0"
          strokeLinecap="round"
          strokeWidth={12}
        />
      </div>

      <Card
        bordered={false}
        style={{
          boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
          borderRadius: 24,
          padding: "10px 10px 30px 10px",
          background: "#ffffff",
          minHeight: 400,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 30 }}>
          <span
            style={{
              background: "#fff0f6",
              color: "#eb2f96",
              padding: "5px 15px",
              borderRadius: "20px",
              fontWeight: 600,
              fontSize: "12px",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            Câu hỏi {currentQuestionIndex + 1} / {shuffledQuestions.length}
          </span>
          <Title
            level={4}
            style={{
              marginTop: 20,
              fontSize: "22px",
              fontWeight: 600,
              color: "#262626",
            }}
          >
            {currentQuestion.questionText}
          </Title>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {currentQuestion.options.map((opt, index) => {
            const isSelected = currentSelectedScore === opt.score;
            return (
              <div
                key={index}
                onClick={() => handleSelectOption(opt.score)}
                style={{
                  padding: "18px 24px",
                  border: isSelected
                    ? "2px solid #ff8e53"
                    : "2px solid transparent",
                  borderRadius: "16px",
                  background: isSelected ? "#fff2e8" : "#f5f5f5",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "16px",
                  fontWeight: isSelected ? 500 : 400,
                  color: isSelected ? "#d4380d" : "#595959",
                  transform: isSelected ? "scale(1.02)" : "scale(1)",
                }}
                className="option-card"
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: isSelected
                      ? "6px solid #ff8e53"
                      : "2px solid #d9d9d9",
                    marginRight: 15,
                    flexShrink: 0,
                    background: "#fff",
                  }}
                />
                {opt.label}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 40, padding: "0 10px" }}>
          <Row gutter={16}>
            <Col span={10}>
              <Button
                size="large"
                block
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
                disabled={currentQuestionIndex === 0}
                style={{
                  color: "#8c8c8c",
                  height: 50,
                  borderRadius: 12,
                  background: "transparent",
                }}
              >
                Quay lại
              </Button>
            </Col>
            <Col span={14}>
              <Button
                type="primary"
                size="large"
                block
                onClick={handleNext}
                disabled={currentSelectedScore === undefined}
                icon={<ArrowRightOutlined />}
                shape="round"
                style={{
                  height: 50,
                  boxShadow:
                    currentSelectedScore !== undefined
                      ? "0 4px 14px rgba(255, 142, 83, 0.4)"
                      : "none",
                  background:
                    currentSelectedScore !== undefined
                      ? "linear-gradient(45deg, #ff8e53, #ff6b6b)"
                      : undefined,
                  border: "none",
                }}
              >
                {currentQuestionIndex === shuffledQuestions.length - 1
                  ? "Hoàn tất"
                  : "Tiếp theo"}
              </Button>
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );
};

export default EQTest;
